import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, fileUrl } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/i18n';
import HorizontalScrollRow from '@/components/HorizontalScrollRow';
import { ChevronLeft, MapPin, Users, Heart, MessageCircle, Shield, Twitch, Youtube, Music, MessageSquare, ImagePlus, Store } from 'lucide-react';
import AddFriendButton from '@/components/AddFriendButton';
import FollowButton from '@/components/FollowButton';
import ReportButton from '@/components/ReportButton';

function StatTile({ icon: Icon, value, label, color }) {
  return (
    <div className="glass rounded-2xl p-5">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl grid place-items-center" style={{ background: `${color}22`, color }}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="font-display font-black text-3xl tracking-tighter leading-none">{value ?? 0}</div>
          <div className="text-[11px] uppercase tracking-wider text-white/50 mt-1">{label}</div>
        </div>
      </div>
    </div>
  );
}

const socialIcons = [
  { key: 'discord', icon: MessageSquare, color: '#5865F2', urlPrefix: '' },
  { key: 'twitch', icon: Twitch, color: '#9146FF', urlPrefix: 'https://twitch.tv/' },
  { key: 'youtube', icon: Youtube, color: '#FF0000', urlPrefix: '' },
  { key: 'tiktok', icon: Music, color: '#00F2EA', urlPrefix: 'https://tiktok.com/@' },
];

export default function PublicProfile() {
  const { userId } = useParams();
  const { user: me } = useAuth();
  const { t } = useI18n();
  const [profile, setProfile] = useState(null);
  const [mediaStats, setMediaStats] = useState(null);
  const [mediaPosts, setMediaPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api.get(`/users/${userId}/public`)
      .then(({ data }) => setProfile(data))
      .catch((e) => setError(e.response?.data?.detail || 'Not found'))
      .finally(() => setLoading(false));
    api.get(`/media/stats/${userId}`)
      .then(({ data }) => setMediaStats(data))
      .catch((err) => { console.warn('[PublicProfile] media stats failed:', err); setMediaStats(null); });
    api.get('/media', { params: { user_id: userId, sort: 'recent' } })
      .then(({ data }) => setMediaPosts(data || []))
      .catch((err) => { console.warn('[PublicProfile] media posts failed:', err); setMediaPosts([]); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  if (loading) return <div className="min-h-screen grid place-items-center text-white/60">{t('common.loading')}</div>;
  if (error || !profile) return <div className="min-h-screen grid place-items-center text-white/60">{error || 'Not found'}</div>;

  const { user: u, stats, spots } = profile;
  const CATEGORIES = [
    { id: 'car_meet',    label: 'Car Meet',     short: 'CM',      color: '#00F0FF' },
    { id: 'viewpoint',   label: 'Point de vue', short: 'Vue',     color: '#9DEDFF' },
    { id: 'parking',     label: 'Parking',      short: 'Parking', color: '#FFD479' },
    { id: 'gas_station', label: 'Station',      short: 'Essence', color: '#10B981' },
    { id: 's365',        label: '365',          short: '365',     color: '#FF7A00' },
    { id: 'spot',        label: 'Spot',         short: 'Spot',    color: '#A78BFA' },
  ];
  const catInfo = (id) => CATEGORIES.find((c) => c.id === id) || { id, label: id, short: id, color: '#FFFFFF' };
  const isMe = me && me !== false && me.id === u.id;
  const socials = u.socials || {};

  return (
    <div className="min-h-screen pt-24 pb-24 px-6 bg-[#0A0A0A] text-white">
      <div className="max-w-5xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-white/50 hover:text-white" data-testid="public-profile-back">
          <ChevronLeft className="w-3 h-3" /> {t('nav.home')}
        </Link>

        <div className="mt-4 glass rounded-3xl p-6 md:p-8 relative overflow-hidden">
          <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full blur-3xl opacity-30" style={{ background: '#FF007F' }} />
          <div className="absolute -bottom-20 -left-20 w-72 h-72 rounded-full blur-3xl opacity-20" style={{ background: '#00F0FF' }} />
          <div className="relative flex flex-col md:flex-row md:items-center gap-6">
            <div className="w-24 h-24 rounded-2xl overflow-hidden">
              {u.avatar_url ? (
                <img src={fileUrl(u.avatar_url)} alt={u.name} className="w-full h-full object-cover" data-testid="public-profile-avatar" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-[#FF007F] to-[#00F0FF] grid place-items-center font-display font-black text-4xl text-black">
                  {(u.name || '?')[0].toUpperCase()}
                </div>
              )}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 flex-wrap">
                <h1 className="font-display text-3xl md:text-5xl font-black uppercase tracking-tighter" data-testid="public-profile-name">{u.name}</h1>
                {u.role === 'admin' && (
                  <span className="px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.2em] border border-[#FF007F] text-[#FF007F] bg-[#FF007F]/10 inline-flex items-center gap-1">
                    <Shield className="w-3 h-3" /> Admin
                  </span>
                )}
              </div>
              {u.gamer_tag && (
                <div className="text-white/60 text-sm mt-1" data-testid="public-profile-gamertag">
                  <span className="text-white/40 uppercase tracking-wider text-[10px] mr-2">{t('profile.edit.gamer_tag')}</span> {u.gamer_tag}
                </div>
              )}
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {socialIcons.filter((s) => socials[s.key]).map((s) => (
                  <a
                    key={s.key}
                    href={s.urlPrefix ? `${s.urlPrefix}${socials[s.key]}` : '#'}
                    target={s.urlPrefix ? '_blank' : undefined}
                    rel="noreferrer"
                    onClick={(e) => { if (!s.urlPrefix) e.preventDefault(); }}
                    className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-white/5 border border-white/10 hover:bg-white/10 transition"
                    style={{ color: s.color }}
                    data-testid={`public-profile-social-${s.key}`}
                  >
                    <s.icon className="w-3 h-3" /> {socials[s.key]}
                  </a>
                ))}
              </div>
            </div>
            {!isMe && (
              <div data-testid="public-profile-actions" className="flex flex-col gap-2 items-end">
                <FollowButton userId={u.id} initialCount={stats.followers_count} />
                <AddFriendButton userId={u.id} userName={u.name} variant="pill" />
                <ReportButton targetId={u.id} targetName={u.name} context={`profile`} variant="pill" />
              </div>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="public-profile-stats">
          <StatTile icon={MapPin} value={stats.spots_count} label={t('profile.stats.spots')} color="#FF007F" />
          <StatTile icon={Users} value={stats.car_meets} label="Car Meets" color="#00F0FF" />
          <StatTile icon={MessageCircle} value={stats.comments_count} label={t('profile.stats.comments') || 'Commentaires'} color="#9DEDFF" />
          <StatTile icon={Heart} value={stats.likes_received} label={t('profile.stats.likes')} color="#FFD479" />
          <StatTile icon={Users} value={stats.followers_count} label={t('leaderboard.followers')} color="#A78BFA" />
        </div>

        {mediaStats && (mediaStats.posts > 0 || mediaStats.likes > 0) && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="public-profile-media-stats">
            <StatTile icon={ImagePlus} value={mediaStats.posts} label={t('public_profile.media.posts') || 'Médias publiés'} color="#FF007F" />
            <StatTile icon={Heart} value={mediaStats.likes} label={t('public_profile.media.likes') || 'Likes Média'} color="#FF007F" />
            <StatTile icon={MessageCircle} value={mediaStats.comments} label={t('public_profile.media.comments') || 'Commentaires reçus'} color="#00F0FF" />
            <StatTile icon={Heart} value={mediaStats.reactions} label={t('public_profile.media.reactions') || 'Réactions'} color="#FFD479" />
          </div>
        )}

        <div className="mt-10">
          <div className="flex items-end justify-between mb-4">
            <h2 className="font-display text-2xl lg:text-3xl font-black uppercase tracking-tighter">{t('public_profile.spots_of')} {u.name}</h2>
            <div className="flex items-center gap-2 text-xs flex-wrap justify-end" data-testid="public-profile-cat-summary">
              {CATEGORIES.map((c) => {
                const n = spots.filter((s) => s.category === c.id).length;
                return (
                  <div key={c.id} className="inline-flex items-center gap-1.5 text-white/60" data-testid={`public-profile-cat-${c.id}`}>
                    <span className="w-2 h-2 rounded-full" style={{ background: c.color, boxShadow: `0 0 8px ${c.color}` }} />
                    {n} {c.short}
                  </div>
                );
              })}
            </div>
          </div>

          {spots.length === 0 ? (
            <div className="glass rounded-2xl p-10 text-center" data-testid="public-profile-empty">
              <MapPin className="w-8 h-8 mx-auto text-white/30" />
              <div className="text-white/50 text-sm mt-3">{t('public_profile.no_spots')}</div>
            </div>
          ) : (
            <HorizontalScrollRow testId="public-profile-spots-row" ariaLabel={t('public_profile.spots') || 'Spots'}>
              {spots.map((s) => (
                <Link
                  key={s.id}
                  to={`/maps/fh6`}
                  data-testid={`public-profile-spot-${s.id}`}
                  className="snap-start shrink-0 w-[280px] group glass rounded-2xl overflow-hidden hover:border-white/30 transition-all"
                >
                  <div className="relative h-36 bg-black/40">
                    {s.image_path ? (
                      <img src={fileUrl(s.image_path)} alt={s.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full grid place-items-center text-white/20">
                        {s.category === 'car_meet' ? <Users className="w-8 h-8" /> : <Camera className="w-8 h-8" />}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    {(() => {
                      const ci = catInfo(s.category);
                      return (
                        <span
                          data-testid={`public-profile-spot-badge-${s.id}`}
                          className="absolute top-3 left-3 px-2.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest border backdrop-blur-md"
                          style={{
                            borderColor: ci.color,
                            color: ci.color,
                            background: 'rgba(0,0,0,0.5)',
                          }}
                        >
                          {ci.label}
                        </span>
                      );
                    })()}
                  </div>
                  <div className="p-3">
                    <div className="font-display font-bold text-sm uppercase tracking-tight truncate">{s.title}</div>
                    <div className="text-[11px] text-white/40 mt-1 line-clamp-1">{s.description || '—'}</div>
                    <div className="flex items-center gap-3 text-[11px] text-white/50 mt-2">
                      <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3" />{s.likes_count}</span>
                      <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" />{s.comments_count || 0}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </HorizontalScrollRow>
          )}
        </div>

        {/* Publications (Media posts: screenshots & vidéos) */}
        {mediaPosts.length > 0 && (
          <div className="mt-10" data-testid="public-profile-media">
            <h2 className="font-display text-2xl font-black uppercase tracking-tight mb-4 inline-flex items-center gap-2">
              <ImagePlus className="w-5 h-5 text-[#FF007F]" /> {t('public_profile.publications') || 'Publications'}
              <span className="text-white/40 text-base font-bold ml-1">({mediaPosts.length})</span>
            </h2>
            <HorizontalScrollRow testId="public-profile-media-row" ariaLabel={t('public_profile.publications') || 'Publications'}>
              {mediaPosts.map((m) => (
                <Link
                  key={m.id}
                  to="/media"
                  data-testid={`public-profile-media-${m.id}`}
                  className="snap-start shrink-0 w-[280px] group glass rounded-2xl overflow-hidden hover:border-white/30 transition-all"
                >
                  <div className="relative aspect-video bg-black/40">
                    {m.kind === 'image' && m.image_path && (
                      <img src={fileUrl(m.image_path)} alt={m.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    )}
                    {m.kind === 'video' && m.video_path && (
                      <video src={fileUrl(m.video_path)} muted className="w-full h-full object-cover" />
                    )}
                    {m.kind === 'link' && (() => {
                      try {
                        const u = new URL(m.external_url || '');
                        if (u.hostname.includes('youtu')) {
                          const id = u.hostname.includes('youtu.be') ? u.pathname.slice(1) : u.searchParams.get('v');
                          if (id) return <img src={`https://img.youtube.com/vi/${id}/hqdefault.jpg`} alt={m.title} className="w-full h-full object-cover" />;
                        }
                      } catch (_e) { /* noop */ }
                      return <div className="w-full h-full grid place-items-center text-[#00F0FF]"><ImagePlus className="w-8 h-8" /></div>;
                    })()}
                    <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/70 text-[9px] uppercase tracking-widest font-bold text-white border border-white/10">
                      {m.kind === 'image' ? 'Screenshot' : m.kind === 'video' ? 'Vidéo' : 'Vidéo (lien)'}
                    </span>
                  </div>
                  <div className="p-3">
                    <div className="font-display font-bold text-sm uppercase tracking-tight truncate">{m.title}</div>
                    <div className="flex items-center gap-3 text-[11px] text-white/50 mt-2">
                      <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3" />{m.likes_count || 0}</span>
                      <span className="inline-flex items-center gap-1"><MessageCircle className="w-3 h-3" />{m.comments_count || 0}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </HorizontalScrollRow>
          </div>
        )}
      </div>
    </div>
  );
}
